'use client';

import React, { useState } from 'react';
import { ImageCardComponent } from '../types/content-components.types.js';
import { useComponentAnimation } from './hooks/useComponentAnimation.js';
import { Image as ImageIcon, ImageOff } from 'lucide-react';

interface ImageCardProps {
  component: ImageCardComponent;
}

export const ImageCard: React.FC<ImageCardProps> = ({ component }) => {
  const { props } = component;
  const { ref, isVisible } = useComponentAnimation(props.animation);
  const [imageError, setImageError] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  const getSizeClass = () => {
    switch (props.size) {
      case 'small':
        return 'max-w-sm';
      case 'medium':
        return 'max-w-md';
      case 'large':
        return 'max-w-lg';
      case 'full':
        return 'w-full';
      default:
        return 'max-w-md';
    }
  };

  const animationClass = isVisible
    ? props.animation === 'fade-in'
      ? 'animate-fadeIn'
      : props.animation === 'slide-up'
      ? 'animate-slideUp'
      : ''
    : '';

  // Placeholder state - imageUrl is null, show generation prompt
  if (!props.imageUrl) {
    return (
      <div
        ref={ref}
        className={`w-full ${getSizeClass()} mx-auto ${animationClass}`}
      >
        <div className="bg-learn-honey-50 border-2 border-dashed border-learn-honey-300 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-learn-honey-200 rounded-lg flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-learn-honey-600" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-learn-honey-900 mb-2">
                📸 Image Needed
              </h4>
              <p className="text-sm text-learn-honey-800 mb-3">
                Generate an image using this prompt, then paste the URL back into the chat:
              </p>
              <div className="bg-white rounded-lg p-3 border border-learn-honey-200">
                <p className="text-sm text-gray-700 font-mono italic leading-relaxed">
                  {props.alt}
                </p>
              </div>
              {props.caption && (
                <p className="text-xs text-learn-honey-700 mt-2 italic">
                  Caption: {props.caption}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`w-full ${getSizeClass()} mx-auto ${animationClass}`}
      style={{ opacity: isVisible ? undefined : 0 }}
    >
      <div className="space-y-2">
        {/* Image */}
        <div className={`relative overflow-hidden ${props.rounded !== false ? 'rounded-xl' : ''} ${props.shadow !== false ? 'shadow-md' : ''}`}>
          {!imageError ? (
            <>
              <img
                src={props.imageUrl}
                alt={props.alt}
                onError={() => setImageError(true)}
                className={`w-full h-auto object-cover ${props.zoom ? 'cursor-zoom-in hover:opacity-95 transition-opacity' : ''}`}
                onClick={() => props.zoom && setIsZoomed(true)}
                loading="lazy"
              />
              {props.zoom && (
                <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                  Click to zoom
                </div>
              )}
            </>
          ) : (
            <div className="w-full aspect-video bg-gray-100 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <ImageOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Image not available</p>
              </div>
            </div>
          )}
        </div>

        {/* Caption */}
        {props.caption && (
          <p className="text-sm text-gray-700 leading-relaxed px-2">
            {props.caption}
          </p>
        )}

        {/* Credit */}
        {props.credit && (
          <p className="text-xs text-gray-500 px-2">
            {props.credit}
          </p>
        )}
      </div>

      {/* Zoom Modal */}
      {isZoomed && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={() => setIsZoomed(false)}
        >
          <img
            src={props.imageUrl!}
            alt={props.alt}
            className="max-w-full max-h-full object-contain"
          />
          <button
            className="absolute top-4 right-4 text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded-lg hover:bg-opacity-70"
            onClick={() => setIsZoomed(false)}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

